import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ArrowLeft, UserPlus, Loader2, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  fetchNotifications,
  respondToFollowRequest,
} from "../../services/notificationService";
import ErrorModal from "../Modal/ErrorModal";
import FetchErrorState from "../Common/FetchErrorState";

const getProfilePicture = (profilePic) => {
  // If it's an object with sizes, use thumbnail or small
  if (profilePic && typeof profilePic === "object") {
    return profilePic.thumbnail || profilePic.small || profilePic.medium;
  }

  // If it's a string (old format), use it directly
  if (typeof profilePic === "string") {
    return profilePic;
  }

  return null;
};

const useIntersectionObserver = (callback, options = {}) => {
  const targetRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          callback();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
        ...options,
      },
    );

    observer.observe(target);

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [callback]);

  return targetRef;
};

const FollowRequestsModal = ({ open, onClose }) => {
  const { apiCall } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [followRequests, setFollowRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });
  const [loadError, setLoadError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Fetch follow requests with cursor support
  const loadFollowRequests = useCallback(
    async (reset = false, currentCursor = null) => {
      try {
        if (reset) {
          setLoading(true);
          setCursor(null);
          setLoadError(null);
        } else {
          setIsFetchingMore(true);
        }

        const result = await fetchNotifications(
          apiCall,
          "follow_request",
          20,
          reset ? null : currentCursor,
        );

        const pendingRequests = result.notifications.filter(
          (notif) => notif.status === 0,
        );

        if (reset) {
          setFollowRequests(pendingRequests);
        } else {
          setFollowRequests((prev) => [...prev, ...pendingRequests]);
        }

        setHasMore(result.hasMore);
        setCursor(result.nextCursor);
      } catch (err) {
        setLoadError("We couldn't load your follow requests.");
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
      }
    },
    [apiCall],
  );

  // Load more handler
  const handleLoadMore = useCallback(() => {
    if (hasMore && !isFetchingMore && !loading) {
      loadFollowRequests(false, cursor);
    }
  }, [hasMore, isFetchingMore, loading, loadFollowRequests, cursor]);

  const loadMoreRef = useIntersectionObserver(handleLoadMore);

  // Load follow requests when modal opens
  useEffect(() => {
    if (open) {
      loadFollowRequests(true);
    }
  }, [open, loadFollowRequests]);

  // Handle follow request
  const handleFollowRequest = useCallback(
    async (notificationId, action, senderUsername) => {
      try {
        await respondToFollowRequest(apiCall, notificationId, action);

        setFollowRequests((prev) => {
          const updated = prev.filter((notif) => notif._id !== notificationId);

          if (updated.length === 0) {
            setTimeout(() => {
              onClose();
            }, 1000);
          }

          return updated;
        });

        const message =
          action === "accept"
            ? `You accepted ${senderUsername}'s follow request`
            : `You rejected ${senderUsername}'s follow request`;

        setSuccessMsg(message);
        setTimeout(() => setSuccessMsg(null), 3000);
      } catch (err) {
        setErrorModal({
          isOpen: true,
          message: "Failed to respond to follow request. Please try again.",
        });
      }
    },
    [apiCall, onClose],
  );

  // Format time ago
  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);

    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
    return `${Math.floor(seconds / 604800)}w`;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: isMobile ? "0" : "16px",
            maxHeight: isMobile ? "100dvh" : "80vh",
            height: isMobile ? "100dvh" : "auto",
            margin: isMobile ? "0" : "32px",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "1px solid #e5e7eb",
            position: "sticky",
            top: 0,
            backgroundColor: "#ffffff",
            zIndex: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <IconButton
              onClick={onClose}
              size="small"
              style={{ padding: "8px", marginLeft: "-8px" }}
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </IconButton>
            <span style={{ fontSize: "18px", fontWeight: "600" }}>
              Follow Requests
            </span>
          </div>
        </DialogTitle>

        {/* Content */}
        <DialogContent
          style={{
            padding: 0,
            backgroundColor: "#ffffff",
            overflowY: "auto",
            height: isMobile ? "calc(100dvh - 65px)" : "auto",
          }}
        >
          {loading && followRequests.length === 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "48px 0",
              }}
            >
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : loadError && followRequests.length === 0 ? (
            <FetchErrorState
              message={loadError}
              onRetry={() => loadFollowRequests(true)}
            />
          ) : followRequests.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "48px 20px",
                color: "#6b7280",
              }}
            >
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                }}
              >
                <UserPlus className="w-8 h-8 text-gray-400" />
              </div>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#111827",
                  marginBottom: "8px",
                }}
              >
                No follow requests
              </h3>
              <p style={{ fontSize: "14px", color: "#6b7280" }}>
                When someone requests to follow you, you'll see it here.
              </p>
            </div>
          ) : (
            <div>
              {followRequests.map((request) => (
                <div
                  key={request._id}
                  style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #f3f4f6",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  {/* Profile Picture */}
                  <div style={{ flexShrink: 0 }}>
                    {(() => {
                      const profilePictureUrl = getProfilePicture(
                        request.senderProfilePic,
                      );
                      return profilePictureUrl ? (
                        <img
                          src={profilePictureUrl}
                          alt={request.senderUsername}
                          style={{
                            width: "56px",
                            height: "56px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "1px solid #e5e7eb",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "56px",
                            height: "56px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid #e5e7eb",
                          }}
                        >
                          {request.senderName ? (
                            <span
                              style={{
                                color: "#64748b",
                                fontWeight: "600",
                                fontSize: "20px",
                              }}
                            >
                              {request.senderName
                                .trim()
                                .split(/\s+/)
                                .slice(0, 2)
                                .map((word) => word.charAt(0).toUpperCase())
                                .join("")}
                            </span>
                          ) : (
                            <svg
                              style={{
                                width: "100%",
                                height: "100%",
                                color: "#cbd5e1",
                                padding: "8px",
                              }}
                              viewBox="0 0 100 100"
                              fill="currentColor"
                            >
                              <circle cx="50" cy="35" r="18" />
                              <path d="M50 58 C 25 58, 10 65, 10 100 L 90 100 C 90 65, 75 58, 50 58 Z" />
                            </svg>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* User info + Buttons container */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {/* User Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: "600",
                          color: "#111827",
                        }}
                      >
                        {request.senderUsername}
                      </div>
                    </div>

                    {/* Buttons on the right */}
                    <div
                      style={{
                        display: "flex",
                        gap: "16px",
                        marginLeft: "auto",
                      }}
                    >
                      <button
                        onClick={() =>
                          handleFollowRequest(
                            request._id,
                            "accept",
                            request.senderUsername,
                          )
                        }
                        style={{
                          padding: "8px 16px",
                          backgroundColor: "#b0d284",
                          color: "black",
                          fontSize: "14px",
                          fontWeight: "600",
                          borderRadius: "8px",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        Accept
                      </button>

                      <button
                        onClick={() =>
                          handleFollowRequest(
                            request._id,
                            "reject",
                            request.senderUsername,
                          )
                        }
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        <X className="w-5 h-5 text-black" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {/* Infinite Scroll Trigger */}
              {hasMore && (
                <div
                  ref={loadMoreRef}
                  style={{
                    padding: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                  <span style={{ fontSize: "14px", color: "#6b7280" }}>
                    Loading more...
                  </span>
                </div>
              )}

              {/* End of List */}
              {!hasMore && followRequests.length > 0 && (
                <div
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    fontSize: "14px",
                    color: "#6b7280",
                  }}
                >
                  You're all caught up!
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      />

      {successMsg && (
        <div className="fixed bottom-6 inset-x-4 z-[70] flex justify-center">
          <div className="w-full max-w-md bg-black text-white text-sm font-medium px-4 py-3 rounded-full shadow-lg text-center">
            {successMsg}
          </div>
        </div>
      )}
    </>
  );
};

export default FollowRequestsModal;
